import { useMemo, useState } from 'react'
import { Trans } from 'react-i18next'

import { ActionFlag, BuilderMenu } from '@makyoapp/makyo-common'

import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useVariableTextSingleCell } from '@/hooks/__project__/useVariableTextSingleCell'
import { Accordion } from '@/components/Accordion/Accordion'
import { BackButton } from '@/components/BackButton'

import MenuBehaviorAccordion from './EditComponents/MenuBehaviourAccordion'
import MenuItemAccordion from './EditComponents/MenuItemAccordion'
import MenuItemEditAccordion from './EditComponents/MenuItemEditAccordion'
import MenuSettingAccordion from './EditComponents/MenuSettingAccordion'

/**
 * MenuPanelEdit is a function component that provides an interface for editing menus in a builder.
 * It also uses the useState hook to manage the state of the openPanel variable, which determines which panel is open.
 */
function MenuPanelEdit({ menu: curMenu }: { menu: BuilderMenu }) {
  const { setMenuAction, menuAction, menuItemAction, setMenuItemAction, menus } = useAppBuilderContext()

  // Find the index of the current menu in the list of menus
  const menuIdx = useMemo(() => {
    return menus.findIndex((m) => m.id === curMenu.id)
  }, [menus, curMenu.id])

  // Current menu
  const menu = menus[menuIdx]
  const menuItem = menuItemAction ? menu.items.find((item) => item.id === menuItemAction.menuItemId) : undefined

  const menuTitle = useVariableTextSingleCell(menu.title)
  const menuItemTitle = useVariableTextSingleCell(menuItem?.title)

  // State for managing the open panel
  const [openPanel, setOpenPanel] = useState('menu-items')

  // The menu editing interface
  return (
    <>
      <div className='flex items-center'>
        <BackButton
          label={<Trans>Back</Trans>}
          bottomBorder
          onClick={() => {
            // Reset the menu item action state when the back button is clicked
            if (menuItemAction?.flag === ActionFlag.Edit) {
              setMenuItemAction(undefined)
            } else {
              setMenuAction(undefined)
            }
          }}
        />
        <div className='w-full border-b h-12 flex-c justify-end ltr:pr-4 rtl:pl-4 bdr text-m-sm text-primary-500'>
          {/* Messages for editing menu items or menus */}
          {menuItemAction?.flag === ActionFlag.Edit && (
            <>
              <Trans>Editing</Trans> {menuItemTitle}
            </>
          )}
          {!menuItemAction && menuAction?.flag === ActionFlag.Edit && (
            <>
              <Trans>Editing</Trans> {menuTitle}
            </>
          )}
        </div>
      </div>
      {/* Accordion for editing menu settings, items */}
      {menuItemAction?.flag !== ActionFlag.Edit && (
        <Accordion openMultiple={false} initialActiveItems={[openPanel]} onSelect={setOpenPanel}>
          <MenuSettingAccordion menu={menu} menuIdx={menuIdx} />
          <MenuItemAccordion menu={menu} menuIdx={menuIdx} />
          <MenuBehaviorAccordion menu={menu} menuIdx={menuIdx} />
        </Accordion>
      )}
      {menuItemAction?.flag === ActionFlag.Edit && menuItem && (
        <MenuItemEditAccordion menu={menu} menuItem={menuItem} menuIdx={menuIdx} />
      )}
    </>
  )
}

export default MenuPanelEdit
